import { useState } from "react";
import { RecordingPlayer } from "../audio/RecordingPlayer";
import { formatBadge } from "../../lib/exam/scoring-types";
import type { TurnScore } from "@a2/core";

export type TurnSummary = {
  turnId: string;
  part: number;
  questionIdx: number;
  promptText: string;
  userText: string;
  feedbackMd: string;
  score: TurnScore;
  audioUrl: string | null;
  attempt: number;
  totalAttempts: number;
  isLatest: boolean;
};

/**
 * The grader emits a single feedbackMd blob with two halves separated
 * by `---`: Dutch coaching line on top (READ ALOUD), English breakdown
 * (🔧 Je zei / Better / Why / Coverage) underneath. Render them
 * distinctly so the student can scan teacher-tone Dutch first, then
 * drop into the analytical English when they want detail.
 */
function FeedbackPanel({ feedbackMd }: { feedbackMd: string }) {
  const [dutch, ...rest] = feedbackMd.split(/^---\s*$/m);
  const english = rest.join("---").trim();
  const dutchTrim = (dutch ?? "").trim();
  return (
    <div className="space-y-3">
      {dutchTrim ? (
        <div>
          <p className="text-xs text-[var(--ink-faint)] mb-1">Feedback van docent</p>
          <p className="font-serif-italic text-base leading-relaxed text-[var(--ink)]">
            {dutchTrim}
          </p>
        </div>
      ) : null}
      {english ? (
        <div>
          <p className="text-xs text-[var(--ink-faint)] mb-1">Toelichting</p>
          <div className="whitespace-pre-wrap text-[var(--ink-muted)] leading-relaxed">
            {english}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TurnBreakdown({ turns }: { turns: TurnSummary[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] divide-y divide-[var(--border)]">
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="text-sm font-medium">Per beurt ({turns.length})</h3>
      </div>
      {turns.map((t) => {
        const total =
          t.score.ad + t.score.wo + t.score.gr + t.score.vl + t.score.helderheid + t.score.uit;
        const isOpen = open === t.turnId;
        const panelId = `turn-${t.turnId}-panel`;
        const showAttemptBadge = t.totalAttempts > 1;
        return (
          <div key={t.turnId}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : t.turnId)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              aria-label={`Beurt deel ${t.part} vraag ${t.questionIdx + 1}${showAttemptBadge ? `, poging ${t.attempt} van ${t.totalAttempts}` : ""}, ${total} van 12. ${isOpen ? "Inklappen" : "Uitklappen"}.`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--panel-soft)] transition-colors"
            >
              <span className="font-mono-badge text-xs text-[var(--ink-faint)] w-12 shrink-0">
                D{t.part}.{t.questionIdx + 1}
              </span>
              {showAttemptBadge ? (
                <span
                  className={`font-mono-badge text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0 ${
                    t.isLatest
                      ? "bg-[var(--accent-soft)] text-[var(--accent)]"
                      : "bg-[var(--panel-soft)] text-[var(--ink-faint)]"
                  }`}
                  aria-hidden
                >
                  Poging {t.attempt}
                </span>
              ) : null}
              <span className="flex-1 truncate text-sm">{t.promptText}</span>
              <span
                className={`font-mono-badge text-xs shrink-0 ${
                  t.isLatest || !showAttemptBadge
                    ? "text-[var(--accent)]"
                    : "text-[var(--ink-faint)]"
                }`}
              >
                {total}/12
              </span>
              <span aria-hidden className="text-[var(--ink-faint)]">{isOpen ? "▾" : "▸"}</span>
            </button>
            {isOpen ? (
              <div id={panelId} className="px-4 pb-4 space-y-4 text-sm">
                <div>
                  <p className="text-xs text-[var(--ink-faint)] mb-1">Uw antwoord</p>
                  <p className="text-[var(--ink-muted)]">
                    {t.userText || "(geen verstaanbare spraak)"}
                  </p>
                  {t.audioUrl ? (
                    <div className="mt-2">
                      <RecordingPlayer src={t.audioUrl} />
                    </div>
                  ) : null}
                </div>
                <FeedbackPanel feedbackMd={t.feedbackMd} />
                <p className="font-mono-badge text-xs text-[var(--ink-faint)]">
                  {formatBadge(t.score).split("\n")[0]}
                </p>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
